import { z } from 'zod';
import { JobRedisStateBaseSchema } from './base.js';

/**
 * Bordereau is unique — 2 distinct job.name values routed to the same
 * processor class, and the payload carries `bordereau_id` directly (no `jobId`
 * field — processor reads it from BullMQ-native `job.id`).
 *
 * DEBT-063 (2026-04-18): Failure state contract cutover.
 *   Before: processor wrote [jobType]_status/error/failed_at into bordereaux.metadata JSONB
 *   After:  processor writes 6-field A6 contract to Redis `job:{id}:bordereau`
 *           Business data (extraction_results, pdf paths) stays in bordereaux.metadata (DB, persistent).
 *
 * BordereauService.getExtractionStatus/getGenerationStatus signatures changed to
 * (jobId, bordereauId): reads Redis for status, DB for business results.
 *
 * TODO(DEBT-069): tighten `.passthrough()` after 7-day stability window (target: 2026-04-25).
 */

export const BordereauExtractPayloadSchema = z
  .object({
    bordereau_id: z.string().uuid(),
    acte_text: z.string().optional(),
    documents: z.array(
      z
        .object({
          document_id: z.string().uuid(),
          name: z.string(),
          category: z.string().optional(),
          // Emitted by BordereauService.startExtraction. Registered explicitly
          // so DEBT-069 strict() tightening doesn't reject the producer payload.
          piece_description: z.string().nullable().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
export type BordereauExtractPayload = z.infer<typeof BordereauExtractPayloadSchema>;

/**
 * Métadonnées de la page de garde (TICKET-259 phase A).
 * Champs saisis manuellement par l'avocat dans le wizard step 3 quand il
 * choisit « PDF avec page de garde ». Tous optionnels — un champ
 * vide/absent est skipped dans le rendu (pas de placeholder « ___ »
 * pour rester propre quand l'avocat veut éditer en post-export).
 *
 * Pré-remplissage automatique depuis case_wiki.parties / cabinet settings
 * = hors scope cette phase (cf. TICKET-260 pour les params avocat).
 */

/**
 * Plafonds de longueur (caractères) par champ de la page de garde.
 *
 * **Source de vérité unique** consommée par les 3 couches :
 * - schéma Zod ci-dessous (`CoverPageMetadataSchema`) — parse worker + API ;
 * - frontend `GenerationStep.tsx` (`maxLength` + compteur visible) ;
 * - validation API `validateGenerate` (safeParse → 400 propre).
 *
 * `notre_avocat` / `avocat_adverse` à 800 (parité avec `client`/`client_adverse`) :
 * ces blocs « avocat » portent un bloc adresse complet (nom + barreau + adresse),
 * parfois plusieurs conseils en litige multi-parties. À 600 le worker rejetait des
 * saisies légitimes — incident bordereau-generate 2026-06-02 (jobs 70237/70241,
 * ZodError `too_big` sur `avocat_adverse`, parse à `bordereau_processor.ts:201`).
 * Rendu PDF = paragraphe fluide multi-page (`PdfGenerationService`), pas de risque
 * de mise en page.
 */
export const COVER_PAGE_FIELD_MAX = {
  juridiction: 300,
  numero_rg: 100,
  affaire: 300,
  audience: 200,
  notre_avocat: 800,
  client: 800,
  avocat_adverse: 800,
  client_adverse: 800,
  fait_a: 120,
  date_signature: 60,
} as const;

export const CoverPageMetadataSchema = z
  .object({
    // ── En-tête (haut de page) — placeholders affichés si vides ──
    /** Juridiction + chambre (ex « Tribunal judiciaire de Paris — 3e chambre »). */
    juridiction: z.string().max(COVER_PAGE_FIELD_MAX.juridiction).nullable().optional(),
    /** Numéro de rôle général (ex « 24/12345 »). */
    numero_rg: z.string().max(COVER_PAGE_FIELD_MAX.numero_rg).nullable().optional(),
    /** Affaire opposant les parties (ex « SAS X C/ M. Y »). */
    affaire: z.string().max(COVER_PAGE_FIELD_MAX.affaire).nullable().optional(),
    /** Date + heure d'audience (texte libre, ex « 12 mars 2026 à 14h »). */
    audience: z.string().max(COVER_PAGE_FIELD_MAX.audience).nullable().optional(),

    // ── « Pièces communiquées par : » (notre côté) — skip si vide ──
    /** Notre avocat (ex « Maître X, Avocat au barreau de Paris, y demeurant… »). */
    notre_avocat: z.string().max(COVER_PAGE_FIELD_MAX.notre_avocat).nullable().optional(),
    /** Notre client représenté (personne physique ou morale, texte libre). */
    client: z.string().max(COVER_PAGE_FIELD_MAX.client).nullable().optional(),

    // ── « À : » (destinataire / partie adverse) — skip si vide ──
    /** Avocat adverse destinataire de la signification. */
    avocat_adverse: z.string().max(COVER_PAGE_FIELD_MAX.avocat_adverse).nullable().optional(),
    /** Client adverse représenté (optionnel). */
    client_adverse: z.string().max(COVER_PAGE_FIELD_MAX.client_adverse).nullable().optional(),

    // ── Bloc signature (bas de page) — placeholders si vides ──
    /** Ville de signature (« Fait à <ville> »). */
    fait_a: z.string().max(COVER_PAGE_FIELD_MAX.fait_a).nullable().optional(),
    /** Date de signature (« le <date> »). */
    date_signature: z.string().max(COVER_PAGE_FIELD_MAX.date_signature).nullable().optional(),
  })
  .passthrough();
export type CoverPageMetadata = z.infer<typeof CoverPageMetadataSchema>;

export const BordereauGeneratePayloadSchema = z
  .object({
    bordereau_id: z.string().uuid(),
    options: z
      .object({
        stamp_id: z.string().nullable().optional(),
        include_index_page: z.boolean().optional(),
        stamp_position: z
          .enum(['top-left', 'top-right', 'bottom-left', 'bottom-right'])
          .optional(),
        stamp_margin_x_mm: z.number().optional(),
        stamp_margin_y_mm: z.number().optional(),
        stamp_x_mm: z.number().optional(),
        stamp_y_mm: z.number().optional(),
        stamp_scale: z.number().optional(),
        stamp_width_mm: z.number().optional(),
        stamp_height_mm: z.number().optional(),
        number_inside_stamp: z.boolean().optional(),
        // Apposer le tampon sur TOUTES les pages de chaque pièce (au lieu de la seule
        // page 1), en réutilisant la position définie pour la pièce. Tamponnage avancé.
        stamp_all_pages: z.boolean().optional(),
        // Per-piece stamp position overrides keyed by document_id (advanced stamping)
        stamp_positions: z.record(
          z.string(),
          z.object({
            x_mm: z.number(),
            y_mm: z.number(),
            scale: z.number().optional(),
          }),
        ).optional(),
        /**
         * Métadonnées injectées sur la page de garde du PDF « avec index ».
         * N'affecte ni le PDF nu (sans cover) ni le ZIP. Si absent ou tous
         * les champs vides, la cover reste identique au comportement
         * pré-TICKET-259 (titre + table seuls).
         */
        cover_page_metadata: CoverPageMetadataSchema.optional(),
      })
      .passthrough(),
  })
  .passthrough();
export type BordereauGeneratePayload = z.infer<typeof BordereauGeneratePayloadSchema>;

/**
 * Shared Redis state. DEBT-063 decides whether to keep the double channel
 * (6 standard fields + legacy `metadata` JSONB) or migrate consumers.
 */
export const BordereauJobDataSchema = JobRedisStateBaseSchema
  .extend({
    bordereau_id: z.string().uuid(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();
export type BordereauJobData = z.infer<typeof BordereauJobDataSchema>;
